import clsx from 'clsx'
import type { DesktopWorkerStatus, ISODate, WorkerJob } from '@app/shared'

import { Desktop } from './icons'
import {
  agoPhrase,
  dayClockPhrase,
  jobDurationPhrase,
  minutesPhrase,
  spanPhrase,
} from './labels'
import { ActionButton, StatPair, StatusPill } from './parts'

// ============================================================================
// THE DESKTOP WORKER — the one piece of LifeOS that runs on a machine the app
// cannot reach, which makes it the place honest degradation matters most.
//
// ONLINE  one row: what the machine is, what it last did in full, an empty
//         queue, the week's throughput, and a way into the log.
// OFFLINE the same row, amber-bordered — and then it EXPANDS INTO THE QUEUE.
//         Three numbered rows with titles and recording times, in the order the
//         PC will run them. "3 jobs are waiting" is a number; "NEU C61 ·
//         Lecture 7, recorded today 7:02am" is a wait you can see the end of.
//         The only action offered is the only one that helps: wake the machine.
// ============================================================================

export function WorkerCard({
  worker,
  today,
  nowMinutes,
}: {
  worker: DesktopWorkerStatus
  today: ISODate
  nowMinutes: number
}) {
  const offline = worker.state === 'offline'
  const queuedSec = worker.queue.reduce((sum, job) => sum + job.durationSec, 0)

  return (
    <article
      className={clsx(
        'flex flex-col gap-[13px] rounded-card border bg-surface px-[17px] py-[15px]',
        offline ? 'border-warning-line' : 'border-line',
      )}
    >
      <div className="flex items-center gap-[14px]">
        <div
          className={clsx(
            'flex size-[38px] flex-none items-center justify-center rounded-row border',
            offline ? 'border-line bg-fill-2' : 'border-success-line bg-success-fill',
          )}
        >
          <Desktop size={19} strokeWidth={1.8} className={offline ? 'text-muted' : 'text-success-dot'} />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[13.5px] font-semibold leading-none">Desktop worker</h3>
            <StatusPill tone={offline ? 'warning' : 'success'} label={offline ? 'OFFLINE' : 'ONLINE'} />
            <span className="text-[10.5px] font-normal leading-none text-microcopy">
              {offline
                ? // A sleeping machine's model and activity are not knowable, so
                  // the line states when it was last seen instead of guessing.
                  `${worker.machine} · last seen ${dayClockPhrase(worker.lastSeenAt, today)}`
                : [worker.machine, worker.model, worker.activity].filter(Boolean).join(' · ')}
            </span>
          </div>
          <p className="text-[11.5px] font-normal leading-[1.4] text-ink-3 text-pretty">
            {offline ? (
              `${worker.queue.length} jobs are waiting for your PC. Nothing is lost — they run in order the next time it wakes up.`
            ) : worker.lastJob ? (
              `Last job: ${worker.lastJob.title}, ${minutesPhrase(worker.lastJob.audioSec)} audio → transcript in ${jobDurationPhrase(worker.lastJob.transcribedInSec)} · finished ${agoPhrase(worker.lastJob.finishedAt, today, nowMinutes)}`
            ) : (
              'No job has run on this machine yet.'
            )}
          </p>
        </div>

        <div className="flex flex-none items-center gap-5">
          {offline ? (
            <>
              <StatPair
                label="QUEUED"
                value={`${worker.queue.length} jobs · ${spanPhrase(queuedSec)} audio`}
                emphatic
              />
              <ActionButton
                label="Wake by Wake-on-LAN"
                waitingFor="Waking the PC needs the job-queue API, which arrives with V1 (the transcription system)."
              />
            </>
          ) : (
            <>
              <StatPair
                label="QUEUE"
                value={worker.queue.length === 0 ? 'empty' : `${worker.queue.length} jobs`}
              />
              <StatPair
                label="JOBS THIS WEEK"
                value={`${worker.jobsThisWeek} · ${spanPhrase(worker.audioThisWeekSec)} audio`}
              />
              <ActionButton
                label="View job log"
                waitingFor="The job log arrives with V1 (the transcription system)."
              />
            </>
          )}
        </div>
      </div>

      {offline && worker.queue.length > 0 && (
        <ol className="flex flex-col rounded-row border border-fill bg-surface-2 px-[6px] py-[5px]">
          {worker.queue.map((job, i) => (
            <QueueRow
              key={job.id}
              job={job}
              position={i + 1}
              last={i === worker.queue.length - 1}
              today={today}
            />
          ))}
        </ol>
      )}
    </article>
  )
}

function QueueRow({
  job,
  position,
  last,
  today,
}: {
  job: WorkerJob
  position: number
  last: boolean
  today: ISODate
}) {
  return (
    <li
      className={clsx(
        'flex items-center gap-[11px] px-[10px] py-[9px]',
        !last && 'border-b border-fill',
      )}
    >
      <span className="flex size-[18px] flex-none items-center justify-center rounded-keycap border border-line bg-surface text-[9.5px] font-semibold leading-none text-muted">
        {position}
      </span>
      <span className="min-w-0 flex-1 truncate text-[11.5px] font-medium leading-none">
        {/* Lectures are measured as audio; a voice memo is just a few minutes. */}
        {job.title} · {minutesPhrase(job.durationSec)}
        {job.kind === 'lecture' ? ' audio' : ''}
      </span>
      <span className="flex-none text-[10.5px] font-normal leading-none text-microcopy">
        recorded {dayClockPhrase(job.recordedAt, today)}
      </span>
      <span className="flex-none rounded-pill border border-line bg-surface px-2 py-[2px] text-[9.5px] font-semibold uppercase leading-[1.7] text-muted">
        {job.work}
      </span>
    </li>
  )
}
